import { useEffect, useState, useSyncExternalStore } from "react";

import { api } from "~/api/client";
import type { Collision, Suspect } from "~/api/types";
import { why } from "~/api/why";
import { useViewer } from "~/Viewer";
import { Spellings } from "./Spellings";
import {
  count,
  onePress,
  latest,
  pending,
  type Seen,
  type Write,
  Writes,
} from "./writes";

import s from "./Tidy.module.css";

const FOLDED = "That one was folded into another act, so it cannot be undone.";

/** The newest undo pressed for `verdict`, if any. */
function undoOf(seen: Seen, verdict: Write): Write | undefined {
  for (let at = seen.writes.length - 1; at >= 0; at -= 1) {
    const write = seen.writes[at];
    if (write?.kind === "undo" && write.takes === verdict.seq) return write;
  }
  return undefined;
}

/** Pressed and not refused or cancelled: on its way, or accepted. */
function inPlay(write: Write): boolean {
  return pending(write) || write.state === "landed";
}

/**
 * Whether a verdict still answers its row, which is what takes the row off the screen.
 *
 * An undo that is on its way puts the row back at once. One the server answered with
 * `undone: 0`, or refused, put nothing back, so the verdict stands again and the row leaves the
 * screen a second time.
 */
function standing(seen: Seen, verdict: Write): boolean {
  if (verdict.kind !== "verdict" || !inPlay(verdict)) return false;
  const undo = undoOf(seen, verdict);
  if (!undo) return true;
  if (pending(undo)) return false;
  return undo.state === "landed" ? undo.answer?.undone === 0 : true;
}

/**
 * Whether Undo can still take a verdict back.
 *
 * Not once an undo of it is on its way or landed. `undone: 0` included, because the server has
 * said the row was folded away and asking again gets the same answer. A refused undo leaves
 * the verdict where it was, so it can be asked again.
 */
function undoable(seen: Seen, verdict: Write): boolean {
  if (verdict.kind !== "verdict" || !inPlay(verdict)) return false;
  const undo = undoOf(seen, verdict);
  return !undo || undo.state === "refused" || undo.state === "cancelled";
}

/**
 * The sentence for a row the server refused, while that refusal is the row's latest word.
 *
 * Not when the editor had already pressed Undo on it: they took the verdict back, the server
 * agrees, and there is nothing to tell them.
 */
function refusal(seen: Seen, row: number): string | null {
  const verdict = latest(seen, "verdict", row);
  if (verdict?.state !== "refused" || undoOf(seen, verdict)) return null;
  return verdict.said ?? null;
}

/**
 * What the last undo to be answered said, until the editor presses something after it.
 *
 * Keyed on when it was answered rather than when it was pressed: a press made while the undo
 * was still out has not read what it says.
 */
function notice(seen: Seen): string | null {
  let last: Write | undefined;
  for (const write of seen.writes) {
    if (write.kind !== "undo" || write.settled === undefined) continue;
    if (!last || write.settled > (last.settled ?? 0)) last = write;
  }
  const settled = last?.settled;
  if (!last || settled === undefined) return null;
  if (seen.writes.some((write) => write.kind !== "spelling" && write.seq > settled))
    return null;
  if (last.state === "refused") return last.said ?? null;
  return last.state === "landed" && last.answer?.undone === 0 ? FOLDED : null;
}

/**
 * The acts that are not acts, one at a time.
 *
 * A promoter types `DJs that we like!` into a line-up field and it becomes an artist with a
 * page, a slug and a place in the search results. `[ROOM 1]` becomes another. None of it can
 * be fixed by a rule — every rule tried cost a real act, which is written down in
 * `docs/suspect-artists.md` — so the answer is a list somebody reads, and this is the half
 * that makes reading it cheap.
 *
 * Four answers, because there are four things a row can be, and a tick could only ever say
 * one of them.
 *
 * An **artist** — the rule was wrong, `[spunge]` is a band. A **qualifier** — `Hannah Diamond
 * [LIVE]` and `Hannah Diamond [DJ SET]` are one person, so the name is corrected and the
 * bracket moves onto the booking, where the bill still prints it. A **split** — several real
 * acts in one row.
 *
 * The last two are the pair that gets confused, and the line between them is whether anybody
 * is playing. A **placeholder** is a performer whose name we do not have: `Resident DJs`,
 * `Special guest`, `+ support`. Somebody is on that stage. **Neither** is not a performer at
 * all: `[A-Z]` is a set label, `[ROOM 1]` is a room, `All things House on ONE / Club Classics
 * and Disco on TWO.` is a description of the programming. Nobody is on a stage called "all
 * things house on one", so the bill should not print one.
 *
 * The buttons say it that way — "Somebody plays, we don't know who" against "Nobody plays" —
 * because "a placeholder, on the bill" read as "any text that belongs on the bill", which is
 * a different question and the wrong one.
 *
 * One at a time rather than a table. There are 220 and each one is a judgement that needs
 * the name, what it is billed alongside, and why it was suspected — which is four lines of
 * context, and four lines times 220 is a page nobody reads to the end of.
 *
 * A second question lives below this one — `Spellings`, the rows two spellings of one name were
 * folded onto. Same page, because both are "an artist row somebody reads and answers", and both
 * count into the one badge on the bar. Separate answers, recorded under separate keys: a row
 * judged a real artist here can still be two acts, and one judged one act there can still be a
 * name that is not a name.
 */
export function Tidy() {
  const me = useViewer();
  const [queue, setQueue] = useState<Suspect[] | null>(null);
  /**
   * The rows two spellings of one name were folded onto — a different question, answered on
   * this page and counted into the same badge.
   *
   * On the same response as the suspects, so the page draws both sections from one request.
   */
  const [collisions, setCollisions] = useState<Collision[]>([]);
  /**
   * What is in the boxes, per act.
   *
   * Derived during render rather than copied into state by an effect. The effect version was
   * `setName(one.suggestion ?? one.name)` keyed on the act, which is correct and arrives one
   * flush late — so the box was briefly empty, and a test that looked at it in that gap saw
   * nothing. Keyed on the act's id so the boxes start from the suggestion when the queue moves
   * on, and so a row that comes back — refused, or undone — comes back holding what was typed
   * on it rather than what was typed on the row after.
   */
  const [drafts, setDrafts] = useState<
    Record<number, { text: string; tail: string; list: string }>
  >({});
  /** Only the queue that would not load. A refused write is drawn on its row instead. */
  const [failed, setFailed] = useState<string | null>(null);
  /**
   * Every answer pressed on this page, the spellings below included, on its way to the server.
   *
   * Which row is on screen, how far through the batch this is, what Undo takes back and the
   * count are all read off it rather than kept beside it, so a refusal or an undo that lands
   * late moves every one of them at once.
   *
   * Undo keeps the id that was decided about, not the answer's: `qualifier` folds a row into
   * another and the server answers with the *keeper's* id, which is not the row to undo.
   */
  const [writes] = useState(() => new Writes());
  const seen = useSyncExternalStore(writes.subscribe, writes.read);

  useEffect(() => {
    // Not asked for at all unless it will be answered. `/suspects` is the editor's and 403s
    // anybody else, and a page that fires a request it knows will be refused is the same
    // mistake as drawing a control that cannot work.
    if (!me?.isEditor) return;
    let live = true;
    api
      .suspects()
      .then((found) => {
        if (!live) return;
        setQueue(found.suspects);
        setCollisions(found.collisions);
        writes.start(found.waiting);
      })
      .catch((error: unknown) => {
        if (live)
          setFailed(why(error));
      });
    return () => {
      live = false;
    };
  }, [me, writes]);

  // The batch in the server's order, less every row a standing verdict answers. Rows are
  // answered from the top, so the first one left is the row the editor was on — unless a
  // refusal or an undo has put an earlier one back, and then that one comes first, which is
  // where the editor's attention has to go.
  const answered = new Set(
    seen.writes.filter((write) => standing(seen, write)).map((write) => write.row),
  );
  const one = queue?.find((row) => !answered.has(row.artist_id));
  const through = queue
    ? queue.filter((row) => answered.has(row.artist_id)).length
    : 0;
  const waiting = count(seen);
  let last: Write | undefined;
  for (const write of seen.writes) if (undoable(seen, write)) last = write;
  // The suggestion until somebody types over it, which is right for most of the rename cases
  // — the whole decision is "yes, that" — and editable, because the suggestion is a guess
  // from a regular expression and some of them will be wrong.
  // A bracket with nothing in front of it: `[LIVE]`, not `Hannah Diamond [LIVE]`. The
  // server sends the tail either way, and now says which of the two this is.
  //
  // Inferred here as "a tail and no suggestion", it was also true of `Baile Ijó B2B (CXLI,
  // ìyáàlù, OTDELTA) with Nicole Blakk` — four acts whose `B2B` comes from the word between
  // them rather than from a bracket. That row was offered one control, "it describes the act
  // before it", with the split it needed hidden behind the same flag.
  // A row that is half of the act above it needs one control, and the two boxes are wrong
  // for it: the rename box is pre-filled with the group's name, so "Rename to X" folds this
  // row exactly as the fold button does but without collapsing the duo's two rows on the
  // bill — the same press offered twice, one of which leaves the group billed under itself.
  // The split box is worse: "Split into 1 act" on a row whose whole problem is that it was
  // already split off something.
  const member = one?.likely === "member";
  const bare = Boolean(one?.lost_its_name);
  const mine = one ? drafts[one.artist_id] : undefined;
  // Empty when no rule could guess, rather than pre-filled with the name being corrected.
  // Filled with the junk it is meant to replace, the button read `Rename to "A:M RESIDENT
  // DJS ON ROTATIO…"` — an offer to change nothing, dressed as the obvious thing to press.
  const name = mine ? mine.text : (one?.suggestion ?? "");
  const tail = mine ? mine.tail : (one?.qualifier ?? "");
  // The pieces, one per line, because deleting a whole line is one gesture and deleting a
  // comma-separated part of a sentence is three. One line where the separators found nothing,
  // which is the common row and is still worth offering: a split into one act is a rename
  // that also takes the crammed row off the bill.
  const list = mine
    ? mine.list
    : (one?.names.length ? one.names : one ? [one.name] : []).join("\n");
  const edit = (part: Partial<{ text: string; tail: string; list: string }>) =>
    one &&
    setDrafts((was) => ({
      ...was,
      [one.artist_id]: { text: name, tail, list, ...part },
    }));

  /*
   * What the correcting button will actually do, said on the button.
   *
   * It read "Fold in" beside a box labelled "What kind of set", so it looked like it was only
   * for qualifiers — and `- Brockie -`, which is `Brockie` with two stray dashes and nothing
   * else wrong, was being marked "an artist" instead, which keeps the junk name. Saying the
   * outcome is the whole fix: a button that reads `Rename to "Brockie"` needs no explaining.
   *
   * The second box takes whatever the bracket held, not only a kind of set. `DJ ForManKind
   * (Pikes/Ibiza)` is a name and a record label, and both readings are served by the same
   * two boxes: the act is named properly, and the label rides on that booking — or is
   * cleared, if it is not worth printing.
   */
  const short = (text: string) =>
    text.length > 28 ? `${text.slice(0, 27)}…` : text;
  // An `@handle` is who they are, not what kind of set it is. It goes on the artist and the
  // page links it, so the button and the box say that rather than "kind of set".
  const handle = /^@[A-Za-z0-9._]+$/.test(tail.trim());
  /*
   * What the button will do, with the parts that are not English left alone.
   *
   * The button is uppercased by the stylesheet, like every control here, and that is right
   * for `RENAME TO` and wrong for `@AYAKAYAKAYAKA_` — a handle is a string somebody else
   * owns, and shouting it makes it look like a different one. The name and the handle keep
   * their own case; the words around them do not.
   *
   * "Instagram" rather than "on their page", because the question was "how do I record that
   * it's their IG" while the button already said it: whatever the wording was, it was not
   * saying Instagram.
   */
  const doing = !name.trim() ? (
    "Rename"
  ) : (
    <>
      Rename to <span className={s.exact}>“{short(name.trim())}”</span>
      {tail.trim() ? (
        <>
          {" · "}
          {handle ? "Instagram " : ""}
          <span className={s.exact}>{tail.trim()}</span>
        </>
      ) : null}
    </>
  );

  /*
   * Which of the six is probably the answer, so the eye lands on one rather than reading all
   * of them. Six equally-weighted controls is what "not sure what I should do here" looks
   * like.
   *
   * The server decides it, from the rule that caught the row. Deriving it here from the
   * parsed pieces is what pointed at "split into 2 acts" for `All things House on ONE / Club
   * Classics and Disco on TWO.` — a description of two rooms that happens to contain a slash.
   *
   * A bracket is the one shape the server leaves open, because which way it goes depends on
   * what is inside: `[LIVE]` is a note that lost its name and `[A-Z]` is a set label.
   *
   * It is a hint, not a rule: every verdict stays available and none is pre-selected.
   */
  const likely = one?.likely || (bare ? "describes" : "");

  // What a placeholder press would leave on the bill, where that is not the row as it stands.
  // Said on the button for the reason `Rename to "Brockie"` is said on the other one: the
  // press changes the name, and a control that changes a name without saying so is one you
  // have to try in order to understand.
  const unnamed =
    likely === "placeholder" && name.trim() && one && name.trim() !== one.name
      ? [name.trim(), tail.trim()].filter(Boolean).join(" · ")
      : "";

  if (me === undefined) return <main className={s.wrap} aria-busy="true" />;
  if (!me?.isEditor) {
    // Hiding a name takes it off every bill it is on and renaming one folds two acts
    // together. Both are corrections to the calendar everybody reads.
    return <main className={s.wrap}>Not yours.</main>;
  }
  if (failed) return <main className={s.wrap}>{failed}</main>;
  if (!queue) return <main className={s.wrap} aria-busy="true" />;

  const decide = (
    verdict:
      | "artist"
      | "qualifier"
      | "describes"
      | "split"
      | "member"
      | "placeholder"
      | "neither",
    // The row is usually the thing being judged. For a member it may be the *group*
    // instead — folding it into this row rather than the other way round — and `/tidy` takes
    // an artist id, so saying which is the whole of the difference between the two presses.
    instead?: number,
  ) => {
    if (!one) return;
    const row = one.artist_id;
    // `placeholder` carries the boxes too, because a row can be three things at once:
    // `05:00 ? ? ? (live)` is an hour nobody needs, a performer the listing declined to name,
    // and a note about the set. The flag alone answered one of the three and left the bill
    // printing the whole string in grey.
    //
    // Only where they hold something, which most placeholders do not: `Resident DJs` is
    // already named as well as it can be, and sending the boxes empty would be a rename to
    // nothing dressed as a decision.
    const carries = verdict === "placeholder";
    // `member` sends the group's name, which is what the rename box was pre-filled with:
    // the server folds this row into the act that already has it.
    const says =
      verdict === "member"
        ? // The name the fold keeps: the group's when this row is part of it, and this row's
          // own when the group is the billing rather than the act.
          instead
          ? one.name
          : name
        : verdict === "qualifier"
          ? name
          : verdict === "split"
            ? list
            : undefined;
    const note =
      verdict === "qualifier" || verdict === "describes" || verdict === "split"
        ? tail
        : undefined;
    // Everything the request carries is read here, at the press, so a write that waits behind
    // another sends what was on screen when it was pressed rather than what is there by then.
    const artist = instead ?? row;
    const said = carries ? name.trim() || undefined : says;
    const noted = carries ? tail.trim() || undefined : note;
    // The boxes as they stood, so a refused row comes back holding what was sent.
    setDrafts((was) => ({ ...was, [row]: { text: name, tail, list } }));
    writes.press("verdict", row, () => api.tidy(artist, verdict, said, noted));
  };

  /*
   * Take the newest verdict back. One not yet sent is simply not sent. One in flight or landed
   * gets an undo behind it, which the queue drops if the verdict is refused. The row comes
   * back on screen at the press either way; an undo that puts nothing back sends it away again
   * and says why (`notice`).
   */
  const undo = () => {
    const taken = last;
    if (!taken) return;
    if (taken.state === "queued") writes.cancel(taken.seq);
    else
      writes.press(
        "undo",
        taken.row,
        () => api.tidy(taken.row, "undo"),
        taken.seq,
      );
  };

  // A refused undo, or one with nothing to put back, said rather than silently doing nothing.
  const told = notice(seen);
  const refused = one ? refusal(seen, one.artist_id) : null;

  if (!one) {
    return (
      <main className={s.wrap}>
        <h1 className={s.done}>Nothing left in this batch.</h1>
        <p className={s.meta}>
          {waiting === 0
            ? "And none waiting at all."
            : `${waiting} still waiting.`}
        </p>
        {waiting > 0 ? (
          <button
            type="button"
            className={s.again}
            // After the queue drains: `/suspects` asked before the server has the answers
            // still on their way would offer those rows again, as though never answered.
            onClick={() => {
              void writes.drained().then(() => window.location.reload());
            }}
          >
            Fetch the next batch
          </button>
        ) : null}
        {last ? (
          <button type="button" className={s.again} {...onePress(undo)}>
            Undo the last one
          </button>
        ) : null}
        {told ? <p className={s.trouble}>{told}</p> : null}
        {/* Drawn here as well as below, because the name-shape queue empties first and this
            one does not empty with it — a page reading "nothing left in this batch" above
            thirty-six unanswered rows would be saying the opposite of what it means.

            Which of its rows are answered is held in `writes`, not in the section, because
            the section is drawn at a different place in each branch: React mounts a fresh
            one when the batch runs out, and a fresh one keeping its own list would draw an
            answered row again. */}
        <Spellings rows={collisions} writes={writes} />
      </main>
    );
  }

  return (
    <main className={s.wrap}>
      <p className={s.meta}>
        {through + 1} of {queue.length} · {waiting} waiting in all
        {/* Only once there is something to take back. The queue is worked fast and the
            commonest mistake is answering the row you were still reading. */}
        {last ? (
          <>
            {" · "}
            <button type="button" className={s.undo} {...onePress(undo)}>
              Undo
            </button>
          </>
        ) : null}
      </p>

      <h1 className={s.name}>{one.name}</h1>

      <p className={s.why}>
        {one.why} · on {one.bills} {one.bills === 1 ? "bill" : "bills"}
      </p>

      {/* What it is billed alongside, which settles most of them at a glance: `[A-Z]` next to
          a drum and bass all-nighter is obviously a set label rather than a band. */}
      {one.seen_on.length > 0 ? (
        <ul className={s.seen}>
          {/*
           * Keyed on the position, not the title. Titles legitimately repeat — a residency
           * is one title on a dozen nights — and a duplicate key stops React reconciling
           * the list, so lines from the act *before* this one stayed on screen underneath
           * it. The server sends distinct titles now, which makes this belt and braces
           * rather than the fix, and it should stay: two acts can share a bill title.
           */}
          {one.seen_on.map((title, at) => (
            <li key={`${at}-${title}`}>{title}</li>
          ))}
        </ul>
      ) : null}

      {/*
       * The three that need no typing. `neither` is last and reads as a consequence rather
       * than an option, because it takes the name off every bill it is on.
       */}
      <div className={s.actions}>
        <button
          type="button"
          className={s.keep}
          {...onePress(() => decide("artist"))}
        >
          An artist, named right
        </button>
        <button
          type="button"
          className={
            likely === "placeholder" ? `${s.keep} ${s.likely}` : s.keep
          }
          {...onePress(() => decide("placeholder"))}
        >
          Somebody plays, we don’t know who
          {unnamed ? (
            <>
              {" · "}
              <span className={s.exact}>{unnamed}</span>
            </>
          ) : null}
        </button>
        <button
          type="button"
          className={likely === "neither" ? `${s.hide} ${s.likely}` : s.hide}
          {...onePress(() => decide("neither"))}
        >
          Nobody plays — take it off
        </button>
      </div>

      {/*
       * A bracket with no name in front of it is a note that lost its name — the `[LIVE]` in
       * `Blawan + [LIVE] + Mala`, which is one feed title split into three acts. There is no
       * name box because there is no name: the note goes onto the act before it on each
       * bill, which is what the feed's own order says it was about.
       */}
      {member ? null : bare ? (
        <div className={s.renaming}>
          <input
            className={`${s.box} ${s.tail}`}
            value={tail}
            aria-label="What it says"
            placeholder="LIVE, ALL NIGHT LONG…"
            onChange={(e) => edit({ tail: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.repeat) decide("describes");
            }}
          />
          <button
            type="button"
            className={
              likely === "describes" ? `${s.rename} ${s.likely}` : s.rename
            }
            disabled={!tail.trim()}
            {...onePress(() => decide("describes"))}
          >
            It describes the act before it
          </button>
        </div>
      ) : (
        <div className={s.renaming}>
          <input
            className={s.box}
            value={name}
            aria-label="The act's name"
            placeholder="The act's name — fix it here"
            onChange={(e) => edit({ text: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.repeat) decide("qualifier");
            }}
          />
          <input
            className={`${s.box} ${s.tail}`}
            value={tail}
            aria-label="What kind of set"
            placeholder={
              handle ? "Their Instagram" : "LIVE, DJ SET, a label… (optional)"
            }
            onChange={(e) => edit({ tail: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.repeat) decide("qualifier");
            }}
          />
          <button
            type="button"
            className={
              likely === "rename" ? `${s.rename} ${s.likely}` : s.rename
            }
            disabled={
              !name.trim() || (name.trim() === one.name && !tail.trim())
            }
            {...onePress(() => decide("qualifier"))}
          >
            {doing}
          </button>
        </div>
      )}

      {/*
       * One act billed as its own halves — the opposite of the split below, and offered only
       * where the evidence points at it.
       *
       * Songkick tags an event with every artist entity it holds for it, so a duo arrives as
       * three names: the group, then each half under it. The name alone cannot say which is
       * which — `Bloc Party & Interpol` is two bands and `CA7RIEL & Paco Amoroso` is one duo
       * — so what puts a row here is that the half never plays a night the group is not also
       * on. That is evidence rather than proof, which is why it is a button and not a rule.
       */}
      {member && one.suggestion ? (
        <div className={s.splitting}>
          {/*
           * Both directions, because the data cannot say which name is the real act.
           * `CA7RIEL & Paco Amoroso` is a duo and its halves are billing debris;
           * `Macka B & The Roots Ragga Band` is Macka B with his band named, and there the
           * *group* is the debris. Both are `X & Y` with both names on one bill, and
           * offering only the first was confidently wrong half the time.
           *
           * Whichever is pressed, the loser's spelling is kept as an alias of the winner —
           * `absorb` records it — so the next feed that sends it lands on the right act.
           */}
          <button
            type="button"
            className={`${s.rename} ${s.likely}`}
            disabled={!name.trim()}
            {...onePress(() => decide("member"))}
          >
            <span className={s.exact}>“{short(one.name)}”</span> is part of{" "}
            <span className={s.exact}>“{short(name.trim())}”</span>
          </button>
          {one.group_id ? (
            <button
              type="button"
              className={s.rename}
              {...onePress(() =>
                decide("member", one.group_id ?? undefined),
              )}
            >
              <span className={s.exact}>“{short(name.trim())}”</span> is just
              how <span className={s.exact}>“{short(one.name)}”</span> was
              billed
            </button>
          ) : null}
        </div>
      ) : null}

      {/*
       * Several acts in one row. Always here, not only where the separators found more than
       * one piece — they were the gate, and they cannot be complete: `- Ayaka feat. Joy` and
       * `Somebody b2b Somebody` are two acts with no separator this knows about, and the
       * page answered "no split available" to a person who could see two names.
       *
       * Pre-filled with whatever the separators did find, which is one line for most rows
       * and is exactly right for them — a split into one act is a rename that also takes the
       * crammed row off the bill.
       *
       * Every piece is offered, including an intro sentence: `A:M RESIDENT DJS ON ROTATION
       * EVERY FRIDAY; ROSS ANDERSON, + Raul Botella` is a sentence and two people, and which
       * part is the sentence is exactly the judgement being asked for. Deleting the line is
       * the answer.
       */}
      {!bare && !member ? (
        <div className={s.splitting}>
          <textarea
            className={s.lines}
            value={list}
            rows={Math.min(Math.max(one.names.length, 1) + 1, 8)}
            aria-label="One act per line"
            onChange={(e) => edit({ list: e.target.value })}
          />
          <button
            type="button"
            className={
              likely === "split" ? `${s.rename} ${s.likely}` : s.rename
            }
            disabled={!list.trim()}
            {...onePress(() => decide("split"))}
          >
            {((count) => (
              <>
                Split into {count} {count === 1 ? "act" : "acts"}
                {/*
                 * A row can be a note *and* an act. `- Live AV ft. Koch.a` on a bill reading
                 * `… + Bitter Babe + - Live AV ft. Koch.a + AliA` says Bitter Babe is playing
                 * a live AV set *and* that Koch.a is playing with her — so the note goes to
                 * the act before, exactly as Describes would, and the names become acts.
                 */}
                {tail.trim() ? (
                  <>
                    {" · "}
                    <span className={s.exact}>{tail.trim()}</span> on the act
                    before
                  </>
                ) : null}
              </>
            ))(list.split("\n").filter((part) => part.trim()).length)}
          </button>
        </div>
      ) : null}

      {/* Said here rather than swallowed. Every other write on this site is `void`ed, which
          is how a 400 on every press of the follow star read as a CSS bug for three weeks.
          A refusal is drawn under the row it refused, with the rest of the page still there
          to answer it from. */}
      {refused ? <p className={s.trouble}>{refused}</p> : null}
      {told ? <p className={s.trouble}>{told}</p> : null}

      <Spellings rows={collisions} writes={writes} />
    </main>
  );
}
